/**
 * API Client Configuration
 *
 * Provides authenticated HTTP client for backend API calls.
 * Automatically includes JWT access token in Authorization header.
 */

import { fetchAuthSession } from 'aws-amplify/auth';

const API_ENDPOINT = import.meta.env.VITE_API_ENDPOINT;

if (!API_ENDPOINT) {
  throw new Error('VITE_API_ENDPOINT environment variable is required');
}

/**
 * Authenticated fetch wrapper
 *
 * Uses Amplify-managed Cognito JWT tokens for authentication.
 * Automatically includes access token in Authorization header.
 * Throws on HTTP error responses for consistent error handling.
 *
 * @param path API path (e.g., '/v1/users/me')
 * @param options Standard fetch options
 * @returns Response data as JSON
 *
 * @example
 * ```ts
 * const profile = await authenticatedFetch('/v1/users/me');
 * const updated = await authenticatedFetch('/v1/users/me', {
 *   method: 'PUT',
 *   body: JSON.stringify({ name: 'New Name' })
 * });
 * ```
 */
export async function authenticatedFetch<T = unknown>(
  path: string,
  options?: RequestInit
): Promise<T> {
  // Get ID token from Amplify session
  // API Gateway Cognito authorizer validates ID tokens, not access tokens
  const session = await fetchAuthSession();

  const idToken = session.tokens?.idToken?.toString();

  if (!idToken) {
    throw new Error('No ID token available. Please sign in.');
  }

  // Construct full URL
  const url = `${API_ENDPOINT}${path}`;

  // Merge headers and add Authorization
  const headers = new Headers(options?.headers);
  headers.set('Content-Type', 'application/json');
  headers.set('Authorization', `Bearer ${idToken}`);

  // Execute fetch
  const response = await fetch(url, {
    ...options,
    headers,
  });

  // Handle non-2xx responses
  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage = `API error: ${response.status} ${response.statusText}`;

    try {
      const errorJson = JSON.parse(errorText);
      errorMessage = errorJson.message || errorMessage;
    } catch {
      // If not JSON, use text as-is
      if (errorText) {
        errorMessage = errorText;
      }
    }

    throw new Error(errorMessage);
  }

  // Parse JSON response
  // Handle 204 No Content
  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
}

/**
 * Unauthenticated fetch wrapper for public endpoints
 *
 * Used for endpoints that don't require authentication (e.g., activation code verification).
 *
 * @param path API path
 * @param options Standard fetch options
 * @returns Response data as JSON
 *
 * @example
 * ```ts
 * const result = await publicFetch('/v1/auth/activate', {
 *   method: 'POST',
 *   body: JSON.stringify({ email, code })
 * });
 * ```
 */
export async function publicFetch<T = unknown>(
  path: string,
  options?: RequestInit
): Promise<T> {
  const url = `${API_ENDPOINT}${path}`;

  const headers = new Headers(options?.headers);
  headers.set('Content-Type', 'application/json');

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage = `API error: ${response.status} ${response.statusText}`;

    try {
      const errorJson = JSON.parse(errorText);
      errorMessage = errorJson.message || errorMessage;
    } catch {
      if (errorText) {
        errorMessage = errorText;
      }
    }

    throw new Error(errorMessage);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
}
